import React, {useState} from "react";
import NoticesDataList from "../../organisms/noticesDataList/noticesDataList";
import {Card, CardBody} from "reactstrap";
import NoticesListHeader from "../../molecules/noticesListHeader/noticesListHeader";
import NoticesDataListItem from "../../../stores/domainStore/pages/models/noticesDataListItem";
import classNames from "classnames";
import "./listNoticesTemplate.sass";
import {SortOrder} from "../../../shared/enums/sortOrder";
import NoticeDetailModal from "../../organisms/noticeDetailModal/noticeDetailModal";
import NoticeItem from "../../../stores/domainStore/pages/models/noticeItem";
import {OptionMenuKeyValue} from "../../../stores/observables/optionMenuKeyValue";

export interface IListNoticesTemplateProps {
  /**
   * Notices to be displayed on the table.
   */
  notices: NoticesDataListItem[];

  /**
   * Indicating that it is currently navigating to another page.
   */
  isNavigating: boolean;

  /**
   * Indicating that the notices are loading.
   */
  isNoticeListLoading: boolean;

  /**
   * Indicating that the notice detail is loading.
   */
  isNoticeDetailLoading: boolean;

  /**
   * The event callback for clicking the add new notice button.
   */
  createNoticeOnClick: () => void;

  /**
   * The event callback for clicking the edit notice button on the notice detail modal.
   */
  editNoticeOnClick: () => void;

  viewNoticeOnClick: () => void;

  /**
   * The event callback for the navigation to the previous page.
   */
  onPrevPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to the next page.
   */
  onNextPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to a specific page.
   */
  onPageNumberSelected?: (pageNumber: number, pageSize: number) => void;

  /**
   * The length of notices.
   */
  noticesLength: number;

  /**
   * The callback for notices list sort event.
   * If it is not provided, sorting will be based on the existing data.
   */
  onSort?: (
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ) => void;

  /**
   * The callback for notices list page change event.
   * If it is not provided, page change will be based on the existing data.
   */
  onPageChange?: (
    pageNumber: number,
    sizePerPage: number,
    sortField: string,
    sortOrder: SortOrder
  ) => void;

  /**
   * The detail of a notice.
   */
  noticeDetail: NoticeItem;

  /**
   * The method for notices list to load the details of a notice.
   */
  loadNoticeDetail: (noticeId: string) => void;

  /**
   * The page number that should be displayed by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentPageNumber?: number;

  /**
   * The field that should be sorted by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentSortField?: string;

  /**
   * The order that should be sorted by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentSortOrder?: SortOrder;

  /**
   * A list of actions that can be applied to the selected notice.
   * +
   */
  actions: OptionMenuKeyValue[];

  /**
   * Action to be applied to the selected notice.
   * @param action
   */
  onActionSelected: (action: OptionMenuKeyValue) => void;

  /**
   * The selected notice to apply an action to.
   */
  selectedNotice: NoticesDataListItem;

  /**
   * Filter function to filter available actions for the selected notice.
   */
  actionFilterFn?: (
    items: OptionMenuKeyValue[],
    notice: NoticesDataListItem
  ) => OptionMenuKeyValue[];
}

const ListNoticesTemplate: React.FC<IListNoticesTemplateProps> = ({
  notices,
  isNavigating,
  isNoticeListLoading,
  isNoticeDetailLoading,
  createNoticeOnClick,
  editNoticeOnClick,
  onPrevPage,
  onNextPage,
  onPageNumberSelected,
  onSort,
  onPageChange,
  noticesLength,
  noticeDetail,
  loadNoticeDetail,
  currentPageNumber,
  currentSortField,
  currentSortOrder,
  actions,
  onActionSelected,
  selectedNotice,
  actionFilterFn,
  viewNoticeOnClick
}) => {
  const [isNoticeDetailModalOpen, setIsNoticeDetailModalOpen] = useState(false);

  const noticeSelectedHandler = (noticeId: string): void => {
    setIsNoticeDetailModalOpen(true);
    loadNoticeDetail(noticeId);
  };

  return (
    <div className="list-notices-template" data-testid="listNoticesTemplate">
      <NoticeDetailModal
        noticeDetail={noticeDetail}
        isLoading={isNoticeDetailLoading}
        isOpen={isNoticeDetailModalOpen}
        toggle={() => setIsNoticeDetailModalOpen(!isNoticeDetailModalOpen)}
        onEditClick={editNoticeOnClick}
        onViewClick={viewNoticeOnClick}
      />
      <Card>
        <NoticesListHeader
          isLoading={isNoticeListLoading}
          createNoticeOnClick={createNoticeOnClick}
        />
        <CardBody
          style={{
            overflowY: isNoticeListLoading ? "hidden" : "auto"
          }}
        >
          <div
            className={classNames({
              "notices-list-hidden": isNavigating || isNoticeListLoading
            })}
          >
            <NoticesDataList
              notices={notices}
              isLoading={isNoticeListLoading}
              isNavigating={isNavigating}
              onPrevPage={onPrevPage}
              onNextPage={onNextPage}
              onPageNumberSelected={onPageNumberSelected}
              noticesLength={noticesLength}
              onPageChange={onPageChange}
              onSort={onSort}
              onNoticeSelected={noticeSelectedHandler}
              currentPageNumber={currentPageNumber}
              currentSortField={currentSortField}
              currentSortOrder={currentSortOrder}
              actions={actions}
              onActionSelected={onActionSelected}
              selectedNotice={selectedNotice}
              actionFilterFn={actionFilterFn}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ListNoticesTemplate;
